import type {
  RealtimeEventMessage,
  RealtimeAckMessage,
  RealtimeTopicStatus,
} from '../types/realtime.types';
import type { SocketAckPayload } from '../types/socket-ack.type';
import { normalizeTopics } from './socket-subscription.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Checks the generic event envelope before domain-specific validation.
 * @param value - Untrusted decoded socket frame.
 * @returns Whether the frame contains a topic and object payload.
 */
export const isRealtimeEventMessage = (
  value: unknown
): value is RealtimeEventMessage => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.type === 'event' &&
    typeof value.topic === 'string' &&
    isRecord(value.payload)
  );
};

/**
 * Recognizes generic control frames before acknowledgement policy is applied.
 * @param value - Untrusted decoded socket frame.
 * @returns Whether the frame is an acknowledgement, error or heartbeat response.
 */
export const isRealtimeAckMessage = (
  value: unknown
): value is RealtimeAckMessage => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.type === 'ack' || value.type === 'error' || value.type === 'pong'
  );
};

/**
 * Extracts untrusted acknowledgement fields without assuming their shape.
 * @param payload - Control-frame payload.
 * @returns Fields for the subscription policy to validate.
 */
export const getAckPayload = (payload: unknown): SocketAckPayload => ({
  action: isRecord(payload) ? payload.action : undefined,
  topics: isRecord(payload) ? payload.topics : undefined,
  requestedTopics: isRecord(payload) ? payload.requestedTopics : undefined,
  count: isRecord(payload) ? payload.count : undefined,
  message: isRecord(payload) ? payload.message : undefined,
});

/**
 * Selects explicitly acknowledged topics or pending legacy topics.
 * @param payload - Untrusted acknowledgement fields.
 * @param currentStatuses - Current topic subscription state.
 * @returns Normalized topics associated with the acknowledgement.
 */
export const getAckTopics = (
  payload: SocketAckPayload,
  currentStatuses: Record<string, RealtimeTopicStatus>
): string[] => {
  const rawTopics = Array.isArray(payload.topics)
    ? payload.topics
    : payload.requestedTopics;

  if (Array.isArray(rawTopics)) {
    return normalizeTopics(
      rawTopics.filter((topic): topic is string => typeof topic === 'string')
    );
  }

  return Object.entries(currentStatuses)
    .filter(([, topicStatus]) => topicStatus === 'pending')
    .map(([topic]) => topic);
};
